import {
  Box,
  Header,
  SpaceBetween,
  Cards,
  Link,
  ColumnLayout,
  Pagination,
  CollectionPreferences,
  CollectionPreferencesProps,
  Table,
  Button,
  Modal,
  Container,
  Grid,
  StatusIndicator,
} from '@cloudscape-design/components';

import { DashboardViewLayout } from '../../../components/DashboardViewLayout';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { LoadingOrError } from '../../../components/Loading';
import { getPlace } from '../../../common/api/places';
import { Place, PlaceDetail, PlaceList } from '../../../common/types';
import { useEffect, useMemo, useState } from 'react';
import { ContactTable } from '../../../components/dashboard/ContactTable';
import { ListTable } from '../../../components/dashboard/ListTable';
import { removePlacesFromList } from '../../../common/api/lists';
import { AddToListModal } from '../../../components/AddToListModal';
import { useUrlSortingState } from '../../../common/filtering/filters';
import { PhoneNumber } from '../../../components/PhoneNumber';
import { PlaceReviewsInfo } from '../../../components/PlaceReviewsInfo';
import { WebsiteContactTable } from '../../../components/WebsiteContactTable';
import { ZoomInfoContactTable } from '../../../components/ZoomInfoContactTable';
import { StarsIcon } from '../../../components/StarsIcon';
import { ReportTable } from '../../../components/dashboard/ReportTable';
import { PlaceTypeList } from '../../../components/PlaceTypeList';
import { ScorecardTrackingPanel } from '../../../components/dashboard/ScorecardTrackingPanel';

export default function View() {
  const navigate = useNavigate();
  const resourceName = 'Places';
  const endpointName = 'places';
  const { uuid } = useParams({ from: '/dashboard/places_/$uuid' });

  // wait for the meta query to finish before proceeding
  const { isPending, isFetching, error, data, refetch } = useQuery({
    queryKey: [endpointName, uuid],
    queryFn: async () => (uuid ? await getPlace(uuid) : null),
    // 10 minutes
    staleTime: 600_000,
  });

  const loading = (isPending || isFetching) && !error;

  const [now, setNow] = useState(new Date());
  const utcSeconds = now.getTime() + now.getTimezoneOffset() * 60 * 1000;

  return (
    <DashboardViewLayout
      headerVariant="high-contrast"
      breadcrumbs={[
        { text: resourceName, href: '/dashboard/places' },
        { text: data?.name ?? '', href: '' },
      ]}
      header={
        data && (
          <Container
            header={
              <Header
                variant="h1"
                info={
                  data?.business_status ? (
                    data?.business_status === 'OPERATIONAL' ? (
                      <StatusIndicator type="success">Open</StatusIndicator>
                    ) : (
                      <StatusIndicator type="error">Closed</StatusIndicator>
                    )
                  ) : (
                    '-'
                  )
                }
                description={data?.address}
              >
                {data?.name}
              </Header>
            }
          >
            <SpaceBetween size="m">
              <SpaceBetween direction="horizontal" size="xs">
                <Box>{data?.rating?.toFixed(1) ?? '?.?'}</Box>
                <StarsIcon stars={data?.rating ?? undefined} />
                <Link href={data?.cid_url ?? undefined} target="_blank" variant="secondary">
                  {data?.total_review_count?.toLocaleString() ?? '?'} Google reviews
                </Link>
              </SpaceBetween>
              <PlaceTypeList types={data?.types} />
              <ColumnLayout columns={3} variant="text-grid">
                <div>
                  <Box variant="awsui-key-label">Phone Number</Box>
                  <PhoneNumber phoneNumber={data?.phone_number} />
                </div>
                <div>
                  <Box variant="awsui-key-label">Local Time</Box>
                  <Box variant="p">
                    {data?.utc_offset ? (
                      <>
                        {new Date(utcSeconds + data.utc_offset * 60 * 1000).toLocaleTimeString([], {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}{' '}
                        ({data.utc_offset > 0 ? '+' : ''}
                        {data.utc_offset / 60} Hours UTC)
                      </>
                    ) : (
                      '-'
                    )}
                  </Box>
                </div>
                <div>
                  <Box variant="awsui-key-label">Website</Box>
                  <Box variant="p">
                    {data?.website ? (
                      <Link href={data?.website} variant="secondary" external target="_blank">
                        {data?.name}
                      </Link>
                    ) : (
                      '-'
                    )}
                  </Box>
                </div>
              </ColumnLayout>
            </SpaceBetween>
          </Container>
        )
      }
    >
      {loading || error ? (
        <LoadingOrError loading={loading} error={error} retry={refetch} />
      ) : data ? (
        <PlaceView place={data} />
      ) : null}
    </DashboardViewLayout>
  );
}

export type PlaceViewProps = {
  place: PlaceDetail;
};

export function PlaceView(props: PlaceViewProps) {
  const navigate = useNavigate();
  const { place } = props;

  const [now, setNow] = useState(new Date());
  const [sortingField, setSortingField] = useState<string | undefined>(undefined);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | undefined>(undefined);
  const [reportSortingField, setReportSortingField] = useState<string | undefined>(undefined);
  const [reportSortDirection, setReportSortDirection] = useState<'asc' | 'desc' | undefined>(
    undefined,
  );

  const [webContactSortingField, setWebContactSortingField] = useState<string | undefined>(
    undefined,
  );
  const [webContactSortDirection, setWebContactSortDirection] = useState<
    'asc' | 'desc' | undefined
  >(undefined);

  const [ziContactSortingField, setZiContactSortingField] = useState<string | undefined>(
    'contact_accuracy_score',
  );
  const [ziContactSortDirection, setZiContactSortDirection] = useState<
    'asc' | 'desc' | undefined
  >('desc');

  const [listNow, setListNow] = useState(new Date());

  const [selectedItems, setSelectedItems] = useState<PlaceList[]>([]);
  const [addToListOpen, setAddToListOpen] = useState(false);
  const remove = useMutation({
    mutationFn: async (lists: PlaceList[]) => {
      for (const list of lists) {
        await removePlacesFromList(list.uuid, { place_uuids: [place.uuid] });
      }
    },
    onSuccess: () => {
      setListNow(new Date());
    },
  });

  useEffect(() => {
    setSelectedItems([]);
  }, [listNow]);

  const removeFromList = () => {
    remove.mutate(selectedItems);
  };

  return (
    <Grid
      gridDefinition={[
        { colspan: { default: 12, s: 12 } },
        { colspan: { default: 12, s: 12 } },
        { colspan: { default: 12, s: 12 } },
        { colspan: { default: 12, s: 12 } },
        { colspan: { default: 12, s: 12 } },
        { colspan: { default: 12, s: 12 } },
      ]}
    >
      <ScorecardTrackingPanel place={place} />
      <Container>
        <ReportTable
          variant="embedded"
          now={now}
          setNow={setNow}
          sortingField={reportSortingField}
          setSortingField={setReportSortingField}
          sortDirection={reportSortDirection}
          setSortDirection={setReportSortDirection}
          query={{
            tokens: [
              {
                propertyKey: 'place_name',
                value: place.name,
                operator: '=',
              },
            ],
            operation: 'and',
          }}
          hideFilter
          hideColumns={['place_name', 'status', 'contacts']}
        />
      </Container>
      <Container>
        <ContactTable
          variant="embedded"
          now={now}
          setNow={setNow}
          sortingField={sortingField}
          setSortingField={setSortingField}
          sortDirection={sortDirection}
          setSortDirection={setSortDirection}
          query={{
            tokens: [
              {
                propertyKey: 'place_name',
                value: place.name,
                operator: '=',
              },
            ],
            operation: 'and',
          }}
          hideFilter
          hideColumns={['place']}
        />
      </Container>
      <Container>
        <WebsiteContactTable
          variant="embedded"
          uuid={place.uuid}
          now={now}
          setNow={setNow}
          sortingField={webContactSortingField}
          setSortingField={setWebContactSortingField}
          sortDirection={webContactSortDirection}
          setSortDirection={setWebContactSortDirection}
          query={{
            tokens: [{ propertyKey: 'place_name', value: place.name, operator: '=' }],
            operation: 'and',
          }}
          hideFilter
          hideColumns={['place']}
        />
      </Container>

      <Container>
        <ZoomInfoContactTable
          variant="embedded"
          uuid={place.uuid}
          now={now}
          setNow={setNow}
          sortingField={ziContactSortingField}
          setSortingField={setZiContactSortingField}
          sortDirection={ziContactSortDirection}
          setSortDirection={setZiContactSortDirection}
        />
      </Container>

      <Container>
        <ListTable
          variant="embedded"
          now={listNow}
          setNow={setListNow}
          query={{
            tokens: [
              {
                propertyKey: 'place_name',
                value: place.name,
                operator: '=',
              },
            ],
            operation: 'and',
          }}
          hideFilter
          hideColumns={['size', 'created']}
          selectedItems={selectedItems}
          setSelectedItems={setSelectedItems}
          additionalActions={
            <SpaceBetween size="xs" direction="horizontal">
              <Button
                variant="normal"
                iconName="add-plus"
                onClick={() => setAddToListOpen(true)}
                disabled={selectedItems.length !== 0}
              >
                Add to List
              </Button>
              <Button
                variant="primary"
                iconName="subtract-minus"
                onClick={removeFromList}
                disabled={selectedItems.length === 0}
              >
                Remove from List
              </Button>
            </SpaceBetween>
          }
        />
      </Container>
      <AddToListModal
        places={[place]}
        open={addToListOpen}
        setOpen={setAddToListOpen}
        onComplete={() => setListNow(new Date())}
      />
    </Grid>
  );
}
